// src/components/layout/Presence.jsx
// STATUS: active | UPDATED: 2026-09-17
// LOG:
//   2026-09-17  Otto shows here too, with his own mark rather than the live
//               dot. See AgentMark for why that distinction matters.
//   2026-09-10  New. Overlapping avatars for whoever is on your team right now.
// The top right of the header.
//
// Tyler asked for the pattern GitHub uses on a pull request: avatars that
// overlap, yourself included, with something showing you are live. The point is
// not decoration. This product is used by people who work in the same building
// and currently gives them no sign the others exist, and he wants the team
// interacting with each other rather than each person alone with a coach.

import { Avatar, AvatarGroup, Box, Tooltip, HStack } from '@chakra-ui/react'
import { usePresence } from '../../hooks/usePresence'
import { roleLabel } from '../../lib/roles'

// A live dot, drawn once and reused. Two rings so it reads on any background:
// the outer ring is the header behind it, which keeps the dot legible where it
// overlaps the next avatar along.
function LiveDot() {
  return (
    <Box
      position="absolute"
      bottom="-1px"
      right="-1px"
      w="11px"
      h="11px"
      borderRadius="full"
      bg="#22C55E"
      border="2px solid"
      borderColor="card"
      aria-hidden
    />
  )
}

// Otto's mark. Deliberately NOT the green dot the humans get.
//
// Green means somebody beat within the last two minutes, which is a claim
// about a person being reachable. Otto is on because a row says so and he is
// never not on, so the same dot on him would quietly devalue it everywhere
// else. A small square in the ink colour reads as "running" rather than as
// "here", which is the true thing.
function AgentMark() {
  return (
    <Box
      position="absolute"
      bottom="-1px"
      right="-1px"
      w="11px"
      h="11px"
      borderRadius="3px"
      bg="accent"
      border="2px solid"
      borderColor="card"
      aria-hidden
    />
  )
}

function Face({ person, isSelf }) {
  const name = person.display_name || 'Teammate'
  const isAgent = !!person.isAgent
  const label = isAgent
    ? `${name}. ${person.tagline || 'Always on. Not a person.'}${person.job_function ? ` ${person.job_function}.` : ''}`
    : isSelf
      ? `${name} (you), here now`
      : `${name}${person.role ? `, ${roleLabel(person.role)}` : ''}, here now`
  return (
    <Tooltip label={label} openDelay={300} hasArrow>
      <Box position="relative" display="inline-flex">
        <Avatar
          size="sm"
          name={name}
          getInitials={() => person.initials || undefined}
          bg={isAgent ? 'ink' : isSelf ? 'ink' : 'surface'}
          color={isAgent || isSelf ? 'onInk' : 'ink'}
          borderWidth="2px"
          borderStyle="solid"
          borderColor={isAgent ? 'accent' : 'card'}
        />
        {isAgent ? <AgentMark /> : isSelf ? <LiveDot /> : null}
      </Box>
    </Tooltip>
  )
}

// Teammates only. Your own avatar stays where it has always been on the far
// right and stays the thing you tap for the menu, so the header gains a row of
// people without losing the control that was already there.
export default function Presence({ profile }) {
  const { others } = usePresence(profile)
  if (others.length === 0) return null

  return (
    <HStack spacing={2} mr={1}>
      <AvatarGroup size="sm" max={4} spacing="-10px">
        {others.map((p) => (
          <Face key={p.id} person={p} isSelf={false} />
        ))}
      </AvatarGroup>
    </HStack>
  )
}

// Exported so the header can put the same dot on your own avatar.
export { LiveDot }
